import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin, FileCheck, Scale, Gavel, ArrowRight, ArrowLeft, CheckCircle,
  AlertTriangle, Shield, Building2, ChevronRight, Loader2, Star, Clock,
  HeartPulse, FileText, Eye, Plus, Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const PARAM_ICONS = {
  location: MapPin, title: FileCheck, mutation: FileText, dispute: Gavel,
  road_access: ArrowRight, encroachment: AlertTriangle, tax_compliance: Scale,
  documents: FileText, other_concerns: Eye,
};

const GRADE_CONFIG = {
  A: { color: "#556B2F", label: "Excellent", desc: "Property is in strong health" },
  B: { color: "#D4AF37", label: "Good", desc: "Minor issues to address" },
  C: { color: "#F59E0B", label: "Fair", desc: "Several areas need attention" },
  D: { color: "#ef4444", label: "Poor", desc: "Significant risks identified" },
  F: { color: "#ef4444", label: "Critical", desc: "Immediate action required" },
};

export default function PropertyAssessment() {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [parameters, setParameters] = useState([]);
  const [selectedProp, setSelectedProp] = useState("");
  const [currentStep, setCurrentStep] = useState(-1); // -1 = property select
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [report, setReport] = useState(null);
  const [reports, setReports] = useState([]);
  const [showReport, setShowReport] = useState(false);
  const [showTaskCreate, setShowTaskCreate] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [pios, setPios] = useState([]);
  const [taskPio, setTaskPio] = useState("");
  const [activeView, setActiveView] = useState("assess"); // assess | reports

  const fetchData = useCallback(async () => {
    try {
      const [pRes, paramRes, rRes, pioRes] = await Promise.all([
        fetch(`${API}/properties`, { credentials: "include" }),
        fetch(`${API}/assessment/parameters`, { credentials: "include" }),
        fetch(`${API}/assessment/reports`, { credentials: "include" }),
        fetch(`${API}/pios/available`, { credentials: "include" }),
      ]);
      if (pRes.ok) setProperties(await pRes.json());
      if (paramRes.ok) setParameters(await paramRes.json());
      if (rRes.ok) setReports(await rRes.json());
      if (pioRes.ok) setPios(await pioRes.json());
    } catch (e) { console.error(e); }
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAnswer = (qId, value) => {
    setAnswers(prev => ({ ...prev, [qId]: value }));
  };

  const submitAssessment = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/assessment/submit`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ property_id: selectedProp, answers }),
      });
      if (res.ok) {
        const data = await res.json();
        setReport(data);
        setShowReport(true);
        fetchData();
      }
    } catch (e) { console.error(e); }
    setSubmitting(false);
  };

  const createTask = async () => {
    if (!selectedIssue || !taskPio) return;
    try {
      await fetch(`${API}/pio/tasks`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({
          property_id: report?.property_id || selectedProp,
          assessment_id: report?.assessment_id || "",
          pio_id: taskPio,
          title: `Rectify: ${selectedIssue.parameter_label}`,
          description: `${selectedIssue.question}\nAnswer: ${selectedIssue.answer}\nSeverity: ${selectedIssue.severity}`,
          task_type: "investigation",
          priority: selectedIssue.severity === "critical" ? "critical" : selectedIssue.severity,
          source_issue: selectedIssue,
        }),
      });
      setShowTaskCreate(false);
      setSelectedIssue(null);
      setTaskPio("");
    } catch (e) { console.error(e); }
  };

  const currentParam = parameters[currentStep];
  const totalSteps = parameters.length;
  const progress = currentStep >= 0 ? ((currentStep + 1) / totalSteps) * 100 : 0;

  const canProceed = () => {
    if (currentStep < 0) return !!selectedProp;
    if (!currentParam) return true;
    return currentParam.questions.every(q => {
      if (q.type === "text") return true;
      return !!answers[q.id];
    });
  };

  const viewReport = (r) => {
    setReport(r);
    setShowReport(true);
  };

  const getScoreColor = (score) => score >= 80 ? "#556B2F" : score >= 60 ? "#D4AF37" : score >= 40 ? "#F59E0B" : "#ef4444";

  return (
    <div data-testid="property-assessment-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <HeartPulse className="w-5 h-5 text-[#D4AF37]" />
            <h2 className="font-serif text-2xl text-white">Property Health Assessment</h2>
          </div>
          <p className="text-white/40 text-sm font-sans">Evaluate your property health across critical parameters</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 bg-[#11130F] border border-white/10 rounded-sm p-1 w-fit">
        {[{ key: "assess", label: "New Assessment" }, { key: "reports", label: `Reports (${reports.length})` }].map(t => (
          <button key={t.key} data-testid={`assessment-tab-${t.key}`} onClick={() => { setActiveView(t.key); if (t.key === "assess") { setCurrentStep(-1); setAnswers({}); setSelectedProp(""); } }}
            className={`px-4 py-2 text-xs font-sans rounded-sm transition-colors ${activeView === t.key ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20" : "text-white/50 hover:text-white"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Past Reports View */}
      {activeView === "reports" && (
        <div className="space-y-3">
          {reports.length === 0 ? (
            <div className="bg-[#11130F] border border-white/10 rounded-sm p-12 text-center">
              <HeartPulse className="w-12 h-12 text-white/10 mx-auto mb-4" />
              <p className="text-white/40 text-sm font-sans">No assessments yet. Start your first property health check.</p>
            </div>
          ) : reports.map(r => {
            const gc = GRADE_CONFIG[r.health_grade] || GRADE_CONFIG.C;
            return (
              <div key={r.assessment_id} onClick={() => viewReport(r)}
                className="bg-[#11130F] border border-white/10 rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors cursor-pointer group">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-sm flex items-center justify-center border" style={{ borderColor: gc.color + "40", backgroundColor: gc.color + "15" }}>
                      <span className="font-serif text-2xl" style={{ color: gc.color }}>{r.health_grade}</span>
                    </div>
                    <div>
                      <h4 className="font-sans font-medium text-white text-sm">{r.property_name}</h4>
                      <p className="text-white/40 text-xs font-sans">Score: {r.overall_score}/100 | {r.flagged_issues?.length || 0} issues | {new Date(r.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-[#D4AF37] transition-colors" />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Assessment Wizard */}
      {activeView === "assess" && !showReport && (
        <div className="max-w-3xl mx-auto">
          {/* Progress */}
          {currentStep >= 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/40 text-xs font-sans">Step {currentStep + 1} of {totalSteps}</span>
                <span className="text-[#D4AF37] text-xs font-sans">{Math.round(progress)}%</span>
              </div>
              <div className="w-full h-1 bg-white/5 rounded-full overflow-hidden">
                <motion.div className="h-full bg-[#D4AF37] rounded-full" initial={{ width: 0 }} animate={{ width: `${progress}%` }} transition={{ duration: 0.5 }} />
              </div>
            </div>
          )}

          <AnimatePresence mode="wait">
            <motion.div key={currentStep} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }}>
              <div className="bg-[#11130F] border border-white/10 rounded-sm p-6">
                {/* Property Selection */}
                {currentStep === -1 && (
                  <div className="space-y-5">
                    <div className="flex items-center gap-3 mb-2">
                      <Building2 className="w-5 h-5 text-[#D4AF37]" />
                      <h3 className="font-serif text-lg text-white">Select Property to Assess</h3>
                    </div>
                    <p className="text-white/40 text-xs font-sans">Choose a property from your portfolio for comprehensive health evaluation.</p>
                    <Select value={selectedProp} onValueChange={setSelectedProp}>
                      <SelectTrigger data-testid="assessment-property-select" className="bg-white/5 border-white/10 text-white rounded-sm">
                        <SelectValue placeholder="Select property" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#11130F] border-white/10">
                        {properties.map(p => <SelectItem key={p.property_id} value={p.property_id}>{p.name} — {p.address}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Parameter Questions */}
                {currentStep >= 0 && currentParam && (
                  <div className="space-y-5">
                    <div className="flex items-center gap-3 mb-2">
                      {(() => { const Icon = PARAM_ICONS[currentParam.key] || Shield; return <Icon className="w-5 h-5 text-[#D4AF37]" />; })()}
                      <div>
                        <h3 className="font-serif text-lg text-white">{currentParam.label}</h3>
                        <p className="text-white/30 text-[10px] font-sans uppercase tracking-wider">{currentParam.key.replace(/_/g, " ")}</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      {currentParam.questions.map(q => (
                        <div key={q.id} className="bg-white/[0.02] border border-white/5 rounded-sm p-4">
                          <label className="text-white/70 text-sm font-sans block mb-3">{q.text}</label>
                          {q.type === "select" ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {q.options.map((opt, oi) => (
                                <button key={opt} data-testid={`answer-${q.id}-${oi}`}
                                  onClick={() => handleAnswer(q.id, opt)}
                                  className={`text-left px-3 py-2.5 rounded-sm text-xs font-sans transition-all border ${answers[q.id] === opt ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-white/10 text-white/50 hover:border-white/20"}`}>
                                  {opt}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <Input data-testid={`answer-${q.id}`} value={answers[q.id] || ""} onChange={e => handleAnswer(q.id, e.target.value)}
                              placeholder="Describe any concerns..." className="bg-white/5 border-white/10 text-white rounded-sm" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Navigation */}
                <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/10">
                  <Button data-testid="assessment-back" onClick={() => setCurrentStep(Math.max(-1, currentStep - 1))} disabled={currentStep <= -1}
                    variant="outline" className="border-white/10 text-white/50 hover:border-[#D4AF37] rounded-sm disabled:opacity-30">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back
                  </Button>
                  {currentStep < totalSteps - 1 ? (
                    <Button data-testid="assessment-next" onClick={() => setCurrentStep(currentStep + 1)} disabled={!canProceed()}
                      className="bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm disabled:opacity-50">
                      Next <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  ) : (
                    <Button data-testid="assessment-submit" onClick={submitAssessment} disabled={submitting || !canProceed()}
                      className="bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm disabled:opacity-50">
                      {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</> : <>Generate Report <CheckCircle className="w-4 h-4 ml-2" /></>}
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Parameter Overview (mini sidebar) */}
          {currentStep >= 0 && (
            <div className="flex flex-wrap gap-1 mt-4">
              {parameters.map((p, i) => {
                const allAnswered = p.questions.every(q => q.type === "text" || !!answers[q.id]);
                return (
                  <button key={p.key} onClick={() => setCurrentStep(i)}
                    className={`text-[10px] px-2 py-1 rounded-sm font-sans transition-colors ${i === currentStep ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20" : allAnswered ? "bg-[#556B2F]/10 text-[#556B2F] border border-[#556B2F]/20" : "text-white/30 border border-white/5"}`}>
                    {allAnswered && i !== currentStep && <CheckCircle className="w-2.5 h-2.5 inline mr-0.5" />}
                    {p.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Report Dialog */}
      <Dialog open={showReport} onOpenChange={setShowReport}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-xl">Property Health Report</DialogTitle></DialogHeader>
          {report && (
            <div className="space-y-5">
              {/* Score Header */}
              <div className="flex items-center gap-6 bg-white/[0.02] border border-white/10 rounded-sm p-5">
                <div className="w-20 h-20 rounded-sm flex items-center justify-center border-2" style={{ borderColor: (GRADE_CONFIG[report.health_grade]?.color || "#D4AF37") + "60", backgroundColor: (GRADE_CONFIG[report.health_grade]?.color || "#D4AF37") + "10" }}>
                  <div className="text-center">
                    <span className="font-serif text-3xl block" style={{ color: GRADE_CONFIG[report.health_grade]?.color }}>{report.health_grade}</span>
                    <span className="text-white/30 text-[9px] font-sans">{report.overall_score}/100</span>
                  </div>
                </div>
                <div>
                  <h3 className="font-serif text-lg text-white">{report.property_name}</h3>
                  <p className="text-sm font-sans" style={{ color: GRADE_CONFIG[report.health_grade]?.color }}>{GRADE_CONFIG[report.health_grade]?.label} — {GRADE_CONFIG[report.health_grade]?.desc}</p>
                  <p className="text-white/30 text-[10px] font-sans mt-1">{report.flagged_issues?.length || 0} issues identified</p>
                </div>
              </div>

              {/* Parameter Scores */}
              <div>
                <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-sans mb-3">Parameter Breakdown</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {Object.entries(report.param_scores || {}).map(([key, val]) => (
                    <div key={key} className="flex items-center justify-between bg-white/[0.02] border border-white/5 rounded-sm px-3 py-2">
                      <span className="text-white/60 text-xs font-sans">{val.label}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1.5 bg-white/5 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${val.score}%`, backgroundColor: getScoreColor(val.score) }} />
                        </div>
                        <span className="text-xs font-sans font-medium w-8 text-right" style={{ color: getScoreColor(val.score) }}>{val.score}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* AI Summary */}
              {report.ai_summary && (
                <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-sm p-4">
                  <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-sans mb-2">AI Intelligence Summary</p>
                  <p className="text-white/60 text-xs font-sans whitespace-pre-wrap leading-relaxed">
                    {report.ai_summary.replace(/\*\*(.*?)\*\*/g, '$1').replace(/###?\s*/g, '')}
                  </p>
                </div>
              )}

              {/* Flagged Issues */}
              {report.flagged_issues?.length > 0 && (
                <div>
                  <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-sans mb-3">Flagged Issues — Assign PIO for Rectification</p>
                  <div className="space-y-2">
                    {report.flagged_issues.map((issue, i) => {
                      const sevColor = issue.severity === "critical" ? "#ef4444" : issue.severity === "high" ? "#F59E0B" : "#D4AF37";
                      return (
                        <div key={i} className="bg-white/[0.02] border border-white/5 rounded-sm p-3 flex items-center justify-between">
                          <div className="flex items-start gap-2 flex-1">
                            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: sevColor }} />
                            <div>
                              <p className="text-white/60 text-xs font-sans">{issue.question}</p>
                              <p className="text-white/30 text-[10px] font-sans mt-0.5">Answer: {issue.answer}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 ml-2">
                            <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-sans uppercase" style={{ color: sevColor, backgroundColor: sevColor + "15" }}>{issue.severity}</span>
                            <Button data-testid={`assign-task-${i}`} onClick={() => { setSelectedIssue(issue); setShowTaskCreate(true); }}
                              variant="outline" className="border-white/10 text-white/40 hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm text-[10px] h-7 px-2">
                              <Users className="w-3 h-3 mr-1" /> Assign PIO
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Task Creation Dialog */}
      <Dialog open={showTaskCreate} onOpenChange={setShowTaskCreate}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-xl">Assign PIO Task</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {selectedIssue && (
              <div className="bg-white/5 border border-white/10 rounded-sm p-3">
                <p className="text-white/60 text-xs font-sans">{selectedIssue.question}</p>
                <p className="text-white/30 text-[10px] font-sans mt-0.5">Issue: {selectedIssue.answer} ({selectedIssue.severity})</p>
              </div>
            )}
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Assign PIO</label>
              <Select value={taskPio} onValueChange={setTaskPio}>
                <SelectTrigger data-testid="task-pio-select" className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue placeholder="Select PIO" /></SelectTrigger>
                <SelectContent className="bg-[#11130F] border-white/10">
                  {pios.map(p => <SelectItem key={p.pio_id} value={p.pio_id}>{p.name} — {p.specialization}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="submit-task-btn" onClick={createTask} disabled={!taskPio} className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm disabled:opacity-50">
              Create Task & Assign PIO
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
